// Commitments Page for Iron Forums

import { useEffect, useState, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/useAuth';
import { accountabilityGroupsAPI, commitmentsAPI, snapshotsAPI, jarvisAPI } from '../api/client';
import type { ChatMessage } from '../types';
import { ShareGroupPicker, ShareItemControl } from '../components/AccountabilityShare';
import {
  Plus,
  Check,
  CheckCircle,
  Circle,
  Clock,
  X,
  Edit2,
  Trash2,
  Users,
  Target,
  Calendar,
  RefreshCw,
  RotateCcw,
  ChevronDown,
  ChevronRight,
  Flame,
  Sparkles,
  Send,
  ExternalLink,
} from 'lucide-react';
import type { Commitment, SnapshotCategory } from '../types';
import toast from 'react-hot-toast';
import TodaysFocus from '../components/TodaysFocus';
import ConfirmModal from '../components/ConfirmModal';
import ConfettiBurst from '../components/ConfettiBurst';
import MicButton from '../components/MicButton';

// Sort order for active goals without a due date: soonest-recurring first.
const FREQUENCY_ORDER: Record<Commitment['frequency'], number> = {
  daily: 0,
  weekly: 1,
  biweekly: 2,
  monthly: 3,
  one_time: 4,
};

export default function Commitments() {
  const { user } = useAuth();
  const [commitments, setCommitments] = useState<Commitment[]>([]);
  const [categories, setCategories] = useState<SnapshotCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCommitment, setEditingCommitment] = useState<Commitment | null>(null);
  const [activeTab, setActiveTab] = useState<'active' | 'completed'>('active');
  const [celebrateBurst, setCelebrateBurst] = useState(0);

  // Form state
  const [formData, setFormData] = useState({
    description: '',
    category: '',
    frequency: 'one_time' as Commitment['frequency'],
    is_shared: false,
    start_date: '',
    end_date: '',
    due_date: '',
    preferred_time: '08:00',
    preferred_day_of_week: null as number | null,
    reminder_enabled: true,
    reminder_cadence: 'weekly' as 'none' | 'daily' | 'weekly' | 'monthly',
    reminder_time: '09:00',
    scheduling_mode: 'specific' as 'specific' | 'flexible',
    target_count: 1,
    preferred_days: [] as number[],
    show_end_date: false,
  });

  // Advanced options disclosure (auto-expanded when editing so existing
  // scheduling/reminder settings stay visible).
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Modal tab state
  const [modalTab, setModalTab] = useState<'jarvis' | 'manual'>('manual');

  // Jarvis chat state
  const [jarvisMessages, setJarvisMessages] = useState<ChatMessage[]>([]);
  const [jarvisInput, setJarvisInput] = useState('');
  const [jarvisLoading, setJarvisLoading] = useState(false);
  const jarvisChatEndRef = useRef<HTMLDivElement>(null);
  const jarvisInputRef = useRef<HTMLInputElement>(null);
  const modalOverlayRef = useRef<HTMLDivElement>(null);
  const [keyboardOpen, setKeyboardOpen] = useState(false);

  // Post-creation confirmation state
  const [createdCommitment, setCreatedCommitment] = useState<Commitment | null>(null);
  const [shareGroupIds, setShareGroupIds] = useState<number[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const submitInFlightRef = useRef(false);

  // Delete confirmation state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    fetchData();
  }, []);

  // Deep-link from the grow-goal prompt banner: `?new=1` opens the create form.
  useEffect(() => {
    if (searchParams.get('new') === '1') {
      openCreateModal();
      searchParams.delete('new');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [commitmentsData, categoriesData] = await Promise.all([
        commitmentsAPI.getAll(),
        snapshotsAPI.getCategories(),
      ]);
      setCommitments(commitmentsData);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      description: '',
      category: '',
      frequency: 'one_time',
      is_shared: false,
      start_date: '',
      end_date: '',
      due_date: '',
      preferred_time: '08:00',
      preferred_day_of_week: null,
      reminder_enabled: true,
      reminder_cadence: 'weekly',
      reminder_time: '09:00',
      scheduling_mode: 'specific',
      target_count: 1,
      preferred_days: [],
      show_end_date: false,
    });
    setEditingCommitment(null);
    setCreatedCommitment(null);
    setShareGroupIds([]);
    setShowAdvanced(false);
    setModalTab('manual');
    setJarvisMessages([]);
    setJarvisInput('');
  };

  // Load Jarvis greeting when modal opens with Jarvis tab
  useEffect(() => {
    if (showModal && modalTab === 'jarvis' && jarvisMessages.length === 0 && !editingCommitment) {
      const fetchGreeting = async () => {
        setJarvisLoading(true);
        try {
          const data = await jarvisAPI.getCommitmentCreationGreeting();
          setJarvisMessages([{ role: 'assistant', content: data.greeting }]);
        } catch (error) {
          console.error('Error fetching Jarvis greeting:', error);
          setJarvisMessages([{ role: 'assistant', content: "Hey! What would you like to commit to? I'll help you set it up." }]);
        } finally {
          setJarvisLoading(false);
        }
      };
      fetchGreeting();
    }
  }, [showModal, modalTab, jarvisMessages.length, editingCommitment]);

  // Auto-scroll Jarvis chat to bottom when new messages arrive
  useEffect(() => {
    jarvisChatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [jarvisMessages, keyboardOpen]);

  // iOS Safari keeps a centered fixed dialog in the layout viewport, then
  // scrolls the focused input into view, which leaves a gap above the keyboard.
  // Pin the sheet to the visual viewport while the keyboard is open.
  useEffect(() => {
    const overlay = modalOverlayRef.current;
    if (!showModal || !overlay) return;
    const viewport = window.visualViewport;
    if (!viewport) return;

    const sync = () => {
      const overlap = window.innerHeight - viewport.height - viewport.offsetTop;
      const touch = window.innerWidth < 1024 || window.matchMedia('(pointer: coarse)').matches;
      const open = touch && overlap > 80;
      setKeyboardOpen(open);
      if (!open) {
        overlay.style.top = '';
        overlay.style.height = '';
        return;
      }
      overlay.style.top = `${viewport.offsetTop}px`;
      overlay.style.height = `${viewport.height}px`;
    };
    sync();
    viewport.addEventListener('resize', sync);
    viewport.addEventListener('scroll', sync);
    return () => {
      viewport.removeEventListener('resize', sync);
      viewport.removeEventListener('scroll', sync);
      overlay.style.top = '';
      overlay.style.height = '';
      setKeyboardOpen(false);
    };
  }, [showModal]);

  // Handle sending message to Jarvis
  const handleJarvisSend = async () => {
    if (!jarvisInput.trim() || jarvisLoading) return;

    const userMessage = jarvisInput.trim();
    setJarvisInput('');
    setJarvisMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setJarvisLoading(true);

    try {
      const conversationHistory = jarvisMessages.map(m => ({
        role: m.role,
        content: m.content
      }));

      const result = await jarvisAPI.createCommitmentChat(userMessage, conversationHistory);

      setJarvisMessages(prev => [...prev, { role: 'assistant', content: result.response }]);

      if (result.action?.type === 'commitment_created') {
        fetchData();
      }
    } catch (error) {
      console.error('Error chatting with Jarvis:', error);
      setJarvisMessages(prev => [...prev, {
        role: 'assistant',
        content: "I encountered an issue. You can try again or use the 'Create Manually' tab."
      }]);
    } finally {
      setJarvisLoading(false);
      // Refocus input so user can keep typing (delay allows React to re-enable the input first)
      setTimeout(() => jarvisInputRef.current?.focus(), 100);
    }
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (commitment: Commitment) => {
    setEditingCommitment(commitment);
    setFormData({
      description: commitment.description,
      category: commitment.category?.toString() || '',
      frequency: commitment.frequency,
      is_shared: commitment.is_shared,
      start_date: commitment.start_date || '',
      end_date: commitment.end_date || '',
      due_date: commitment.due_date || '',
      preferred_time: commitment.preferred_time?.slice(0, 5) || '08:00',
      preferred_day_of_week: commitment.preferred_day_of_week,
      reminder_enabled: commitment.reminder_enabled ?? true,
      reminder_cadence: commitment.reminder_cadence || 'weekly',
      reminder_time: commitment.reminder_time?.slice(0, 5) || '09:00',
      scheduling_mode: commitment.scheduling_mode || 'specific',
      target_count: commitment.target_count || 1,
      preferred_days: commitment.preferred_days || [],
      show_end_date: !!commitment.end_date,
    });
    setShowAdvanced(true);
    setModalTab('manual');  // Go straight to manual form when editing
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitInFlightRef.current) return;
    submitInFlightRef.current = true;
    setIsSaving(true);
    try {
      const data = {
        description: formData.description,
        category: formData.category ? parseInt(formData.category) : null,
        frequency: formData.frequency,
        is_shared: formData.is_shared,
        start_date: formData.start_date || null,
        end_date: formData.show_end_date ? (formData.end_date || null) : null,
        due_date: formData.due_date || null,
        preferred_time: formData.preferred_time || null,
        preferred_day_of_week: formData.preferred_days.length > 0 ? formData.preferred_days[0] : formData.preferred_day_of_week,
        reminder_enabled: formData.reminder_cadence !== 'none',
        reminder_cadence: formData.reminder_cadence,
        reminder_time: formData.reminder_cadence !== 'none' ? (formData.reminder_time || null) : null,
        scheduling_mode: formData.scheduling_mode,
        target_count: formData.scheduling_mode === 'flexible' ? formData.target_count : 1,
        preferred_days: formData.scheduling_mode === 'specific' ? formData.preferred_days : [],
      };

      if (editingCommitment) {
        await commitmentsAPI.update(editingCommitment.id, data);
        setShowModal(false);
        resetForm();
      } else {
        const newCommitment = await commitmentsAPI.create(data);
        if (shareGroupIds.length > 0) {
          try {
            await accountabilityGroupsAPI.setItemShares(
              'grow_goal',
              newCommitment.id,
              shareGroupIds,
            );
          } catch {
            toast.error('Goal saved, but sharing with a group needs retrying.');
          }
        }
        setCreatedCommitment(newCommitment);
      }

      fetchData();
    } catch (error) {
      console.error('Error saving commitment:', error);
    } finally {
      submitInFlightRef.current = false;
      setIsSaving(false);
    }
  };

  const handleComplete = async (id: number) => {
    try {
      await commitmentsAPI.complete(id);
      setCelebrateBurst((n) => n + 1);
      fetchData();
    } catch (error) {
      console.error('Error completing commitment:', error);
    }
  };

  const handleCancel = async (id: number) => {
    try {
      await commitmentsAPI.cancel(id);
      fetchData();
    } catch (error) {
      console.error('Error cancelling commitment:', error);
    }
  };

  const handleUncomplete = async (id: number) => {
    try {
      await commitmentsAPI.uncomplete(id);
      fetchData();
    } catch (error) {
      console.error('Error uncompleting commitment:', error);
    }
  };

  const handleDelete = (id: number) => {
    setPendingDeleteId(id);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (pendingDeleteId === null) return;
    setDeleteConfirmOpen(false);
    try {
      await commitmentsAPI.delete(pendingDeleteId);
      fetchData();
    } catch (error) {
      console.error('Error deleting commitment:', error);
    } finally {
      setPendingDeleteId(null);
    }
  };

  const handleCheckIn = async (id: number, completed: boolean, notes?: string) => {
    try {
      await commitmentsAPI.checkIn(id, completed, notes);
      fetchData();
    } catch (error) {
      console.error('Error checking in:', error);
    }
  };

  const handleUncheckIn = async (id: number) => {
    try {
      await commitmentsAPI.uncheckIn(id);
      fetchData();
    } catch (error) {
      console.error('Error unchecking in:', error);
    }
  };

  const myCommitments = commitments.filter(c => c.user === user?.id);

  const activeCommitments = myCommitments.filter(c =>
    c.status === 'pending' || c.status === 'in_progress'
  );
  const completedCommitments = myCommitments.filter(c =>
    c.status === 'completed' || c.status === 'cancelled'
  );

  // Flat list: goals with a due date first (soonest first), then recurring
  // goals ordered by how often they come around.
  const sortedActive = useMemo(() => {
    return [...activeCommitments].sort((a, b) => {
      if (a.due_date && b.due_date) return a.due_date.localeCompare(b.due_date);
      if (a.due_date) return -1;
      if (b.due_date) return 1;
      const freqDiff = FREQUENCY_ORDER[a.frequency] - FREQUENCY_ORDER[b.frequency];
      if (freqDiff !== 0) return freqDiff;
      return a.description.localeCompare(b.description);
    });
  }, [activeCommitments]);

  const getFrequencyLabel = (frequency: string) => {
    const labels: Record<string, string> = {
      one_time: 'One-time',
      daily: 'Daily',
      weekly: 'Weekly',
      biweekly: 'Biweekly',
      monthly: 'Monthly',
    };
    return labels[frequency] || frequency;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="text-green-500" size={20} />;
      case 'in_progress':
        return <Clock className="text-gold" size={20} />;
      case 'cancelled':
        return <X className="text-gray-400" size={20} />;
      default:
        return <Circle className="text-gray-300" size={20} />;
    }
  };

  // Check if a commitment has been checked in today
  const hasCheckedInToday = (commitment: Commitment): boolean => {
    const todayStr = new Date().toISOString().split('T')[0];
    return commitment.check_ins?.some(ci => ci.date === todayStr && ci.completed) || false;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ConfettiBurst burstId={celebrateBurst} />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-navy">
            Grow Goals
          </h1>
          <p className="text-gray-600">Track your goals and accountability items</p>
        </div>
        <button
          onClick={openCreateModal}
          className="inline-flex items-center gap-2 px-5 py-3 bg-navy text-white rounded-xl hover:bg-navy-dark transition font-semibold"
          aria-label="New Grow Goal"
        >
          <Plus size={20} />
          New Grow Goal
        </button>
      </div>

      {/* Today's Focus */}
      <TodaysFocus
        commitments={myCommitments}
        onCheckIn={handleCheckIn}
        onUncheckIn={handleUncheckIn}
        onComplete={handleComplete}
        onUncomplete={handleUncomplete}
        loading={isLoading}
      />

      {/* Tabs */}
      <div className="flex flex-wrap items-center justify-between gap-x-2 border-b border-gray-200">
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('active')}
            className={`px-4 py-2 font-medium transition ${
              activeTab === 'active'
                ? 'text-navy border-b-2 border-navy'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Active ({activeCommitments.length})
          </button>
          <button
            onClick={() => setActiveTab('completed')}
            className={`px-4 py-2 font-medium transition ${
              activeTab === 'completed'
                ? 'text-navy border-b-2 border-navy'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Completed ({completedCommitments.length})
          </button>
        </div>
        <Link
          to="/accountability-groups"
          className="flex items-center gap-1.5 pb-2 text-sm text-navy hover:text-navy-dark font-medium"
        >
          <Users size={15} />
          Accountability Groups
          <ExternalLink size={13} />
        </Link>
      </div>

      {/* Commitment List */}
      <div className="space-y-4">
        {/* Active Tab - Flat List */}
        {activeTab === 'active' && (
          <>
            {sortedActive.length === 0 ? (
              <div className="bg-white rounded-xl p-8 text-center shadow-sm">
                <Target className="mx-auto text-gray-300" size={48} />
                <p className="text-gray-500 mt-2">No active Grow Goals</p>
                <button
                  onClick={openCreateModal}
                  className="mt-4 text-navy font-medium hover:underline"
                >
                  Create your first Grow Goal
                </button>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                {sortedActive.map((commitment) => (
                  <div
                    key={commitment.id}
                    className="px-5 py-4 border-b border-gray-50 last:border-b-0"
                  >
                    <div className="flex items-start gap-4">
                      {/* Status Icon / Check-in Button */}
                      <div className="pt-1">
                        {commitment.frequency !== 'one_time' && commitment.status !== 'completed' ? (
                          hasCheckedInToday(commitment) ? (
                            <button
                              onClick={() => handleUncheckIn(commitment.id)}
                              className="w-8 h-8 pointer-coarse:w-10 pointer-coarse:h-10 rounded-full bg-green-100 flex items-center justify-center hover:bg-green-200 transition"
                              title="Undo check-in for today"
                            >
                              <CheckCircle className="text-green-600" size={16} />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleCheckIn(commitment.id, true)}
                              className="w-8 h-8 pointer-coarse:w-10 pointer-coarse:h-10 rounded-full border-2 border-green-500 flex items-center justify-center hover:bg-green-50 transition"
                              title="Check in for today"
                            >
                              <CheckCircle className="text-green-500" size={16} />
                            </button>
                          )
                        ) : (
                          getStatusIcon(commitment.status)
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-gray-800">{commitment.description}</p>
                          {/* Streak Badge */}
                          {commitment.streak !== null && commitment.streak > 0 && (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                              commitment.streak >= 7
                                ? 'bg-orange-100 text-orange-700'
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              <Flame size={12} className={commitment.streak >= 7 ? 'text-orange-500' : 'text-gray-500'} />
                              {commitment.streak}
                            </span>
                          )}
                          {/* Checked in today indicator */}
                          {hasCheckedInToday(commitment) && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              <Check size={12} />
                              Done today
                            </span>
                          )}
                        </div>

                        <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-gray-500">
                          {commitment.frequency !== 'one_time' && (
                            <span className="flex items-center gap-1">
                              <RefreshCw size={14} />
                              {getFrequencyLabel(commitment.frequency)}
                            </span>
                          )}

                          {commitment.category_name && (
                            <span className="flex items-center gap-1">
                              <Target size={14} />
                              {commitment.category_name}
                            </span>
                          )}

                          {commitment.due_date && (
                            <span className="flex items-center gap-1">
                              <Calendar size={14} />
                              Due: {new Date(commitment.due_date).toLocaleDateString()}
                            </span>
                          )}

                          {commitment.is_shared && (
                            <span className="flex items-center gap-1 text-navy">
                              <Users size={14} />
                              Facilitator
                            </span>
                          )}
                        </div>

                        {/* Progress bar for recurring commitments */}
                        {commitment.progress && (
                          <div className="mt-3">
                            <div className="flex justify-between gap-2 text-sm text-gray-500 mb-1">
                              <span>Progress</span>
                              <span>{commitment.progress.completed} / {commitment.progress.expected} ({commitment.progress.percentage}%)</span>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-2">
                              <div
                                className="bg-green-500 h-2 rounded-full transition-all"
                                style={{ width: `${commitment.progress.percentage}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      {commitment.status !== 'completed' && commitment.status !== 'cancelled' && (
                        <div className="flex items-center gap-2">
                          {commitment.frequency === 'one_time' && (
                            <button
                              onClick={() => handleComplete(commitment.id)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition"
                              title="Mark complete"
                            >
                              <CheckCircle size={18} />
                            </button>
                          )}
                          <button
                            onClick={() => openEditModal(commitment)}
                            className="p-2 pointer-coarse:p-[11px] text-gray-500 hover:bg-gray-100 rounded-lg transition"
                            title="Edit"
                          >
                            <Edit2 size={18} />
                          </button>
                          <button
                            onClick={() => handleCancel(commitment.id)}
                            className="p-2 pointer-coarse:p-[11px] text-gray-500 hover:bg-gray-100 rounded-lg transition"
                            title="Cancel"
                          >
                            <X size={18} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Completed Tab - Flat List */}
        {activeTab === 'completed' && (
          <>
            {completedCommitments.length === 0 ? (
              <div className="bg-white rounded-xl p-8 text-center shadow-sm">
                <CheckCircle className="mx-auto text-gray-300" size={48} />
                <p className="text-gray-500 mt-2">No completed Grow Goals yet</p>
              </div>
            ) : (
              completedCommitments.map((commitment) => (
                <div
                  key={commitment.id}
                  className="bg-white rounded-xl p-5 shadow-sm"
                >
                  <div className="flex items-start gap-4">
                    <div className="pt-1">
                      {getStatusIcon(commitment.status)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-800">{commitment.description}</p>
                      <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-gray-500">
                        {commitment.category_name && (
                          <span className="flex items-center gap-1">
                            <Target size={14} />
                            {commitment.category_name}
                          </span>
                        )}
                        {commitment.frequency !== 'one_time' && (
                          <span className="flex items-center gap-1">
                            <RefreshCw size={14} />
                            {getFrequencyLabel(commitment.frequency)}
                          </span>
                        )}
                        {commitment.is_shared && (
                          <span className="flex items-center gap-1 text-navy">
                            <Users size={14} />
                            Facilitator
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleUncomplete(commitment.id)}
                        className="p-2 text-navy hover:bg-blue-50 rounded-lg transition"
                        title="Mark as active again"
                      >
                        <RotateCcw size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(commitment.id)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition"
                        title="Delete"
                        aria-label="Delete"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </>
        )}
      </div>

      {/* Create/Edit Modal - Smart Adaptive Form */}
      {showModal && (
        <div
          ref={modalOverlayRef}
          className={`fixed z-50 flex justify-center bg-black/50 transition-opacity duration-200 ${
            keyboardOpen
              ? 'inset-x-0 items-stretch p-2'
              : 'inset-0 items-center px-4 pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]'
          }`}
          role="dialog"
          aria-modal="true"
        >
          <div className={`bg-white rounded-2xl shadow-xl w-full max-w-md flex flex-col transition-all duration-200 ${
            keyboardOpen
              ? 'h-full max-h-full'
              : 'max-h-[min(85vh,calc(100dvh-env(safe-area-inset-top)-env(safe-area-inset-bottom)-2rem))]'
          }`}>
              {createdCommitment ? (
                <div className="p-6 overflow-y-auto text-center">
                  <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="text-green-600" size={32} />
                  </div>
                  <h2 className="text-xl font-bold text-navy mb-2">Grow Goal Created!</h2>

                  <div className="bg-off-white rounded-lg p-4 text-left mt-4 mb-6">
                    <p className="font-medium text-gray-800 mb-2">"{createdCommitment.description}"</p>
                    <div className="space-y-1 text-sm text-gray-600">
                      <p className="flex items-center gap-2">
                        <RefreshCw size={14} />
                        {getFrequencyLabel(createdCommitment.frequency)}
                        {/* Show flexible scheduling info */}
                        {createdCommitment.scheduling_mode === 'flexible' && createdCommitment.target_count > 1 && (
                          <span>- {createdCommitment.target_count}x per {createdCommitment.frequency === 'weekly' ? 'week' : 'month'}</span>
                        )}
                        {/* Show specific days */}
                        {createdCommitment.scheduling_mode === 'specific' && createdCommitment.preferred_days?.length > 0 && (
                          <span>on {createdCommitment.preferred_days.map(d => ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][d]).join(', ')}</span>
                        )}
                        {/* Fallback to old field */}
                        {createdCommitment.frequency === 'weekly' && !createdCommitment.preferred_days?.length && createdCommitment.preferred_day_of_week !== null && (
                          <span>on {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][createdCommitment.preferred_day_of_week]}</span>
                        )}
                        {createdCommitment.preferred_time && (
                          <span>at {(() => {
                            const [h, m] = createdCommitment.preferred_time.split(':').map(Number);
                            const period = h >= 12 ? 'PM' : 'AM';
                            return `${h % 12 || 12}:${m.toString().padStart(2, '0')} ${period}`;
                          })()}</span>
                        )}
                      </p>
                      {createdCommitment.end_date ? (
                        <p className="flex items-center gap-2">
                          <Calendar size={14} />
                          Until {new Date(createdCommitment.end_date).toLocaleDateString()}
                        </p>
                      ) : createdCommitment.frequency !== 'one_time' && (
                        <p className="flex items-center gap-2 text-green-600">
                          <RefreshCw size={14} />
                          Ongoing
                        </p>
                      )}
                      {createdCommitment.reminder_cadence && createdCommitment.reminder_cadence !== 'none' && (
                        <p className="flex items-center gap-2">
                          <Clock size={14} />
                          {createdCommitment.reminder_cadence === 'daily'
                            ? 'Daily reminders'
                            : createdCommitment.reminder_cadence === 'monthly'
                            ? 'Monthly reminders'
                            : 'Weekly reminders'}
                        </p>
                      )}
                      {createdCommitment.is_shared && (
                        <p className="flex items-center gap-2 text-navy">
                          <Users size={14} />
                          Visible to your facilitator
                        </p>
                      )}
                    </div>
                    <div className="mt-3 border-t border-gray-200 pt-3">
                      <ShareItemControl itemType="grow_goal" objectId={createdCommitment.id} />
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        resetForm();
                      }}
                      className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition"
                    >
                      Add Another
                    </button>
                    <button
                      onClick={() => {
                        setShowModal(false);
                        resetForm();
                      }}
                      className="flex-1 py-3 px-4 bg-navy text-white rounded-lg font-medium hover:bg-navy-dark transition"
                    >
                      Done
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                    <h2 className="text-lg font-bold text-navy">
                      {editingCommitment ? 'Edit Grow Goal' : 'New Grow Goal'}
                    </h2>
                    <button
                      onClick={() => {
                        setShowModal(false);
                        resetForm();
                      }}
                      className="p-2 hover:bg-gray-100 rounded-lg transition"
                      aria-label="Close"
                    >
                      <X size={20} />
                    </button>
                  </div>

                  {!editingCommitment && (
                    <div className="flex border-b border-gray-100">
                      <button
                        type="button"
                        onClick={() => setModalTab('manual')}
                        className={`flex-1 py-3 text-sm font-medium border-b-2 transition ${
                          modalTab === 'manual'
                            ? 'border-navy text-navy'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        Create Manually
                      </button>
                      <button
                        type="button"
                        onClick={() => setModalTab('jarvis')}
                        className={`flex-1 py-3 text-sm font-medium border-b-2 transition flex items-center justify-center gap-2 ${
                          modalTab === 'jarvis'
                            ? 'border-navy text-navy'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        <Sparkles size={16} />
                        Ask IF
                      </button>
                    </div>
                  )}

                  {(editingCommitment || modalTab === 'manual') && (
                  <form onSubmit={handleSubmit} className="flex-1 min-h-0 flex flex-col">
                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {/* Description */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        What are you committing to?
                      </label>
                      <div className="relative">
                        <textarea
                          value={formData.description}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                          required
                          rows={3}
                          className="w-full px-4 py-3 pr-12 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent outline-none resize-none"
                          placeholder="e.g., Have a date night with my spouse every week"
                        />
                        <MicButton
                          value={formData.description}
                          onChange={(next) => setFormData({ ...formData, description: next })}
                          className="absolute bottom-2 right-2"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        What change are you committing to before the next forum?
                      </p>
                    </div>

                    {/* Frequency */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        How often?
                      </label>
                      <div className="grid grid-cols-3 gap-2">
                        {(['one_time', 'daily', 'weekly', 'biweekly', 'monthly'] as const).map((freq) => (
                          <button
                            key={freq}
                            type="button"
                            onClick={() => setFormData({
                              ...formData,
                              frequency: freq,
                              scheduling_mode: 'specific',
                              target_count: 1,
                              preferred_days: [],
                            })}
                            className={`px-3 py-2 rounded-lg border text-sm transition ${
                              formData.frequency === freq
                                ? 'border-navy bg-navy/5 text-navy'
                                : 'border-gray-200 text-gray-600 hover:border-gray-300'
                            }`}
                          >
                            {getFrequencyLabel(freq)}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Due date for one-time */}
                    {formData.frequency === 'one_time' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Due Date
                        </label>
                        <input
                          type="date"
                          value={formData.due_date}
                          onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent outline-none"
                        />
                        <p className="text-xs text-gray-500 mt-1">Default: end of this week</p>
                      </div>
                    )}

                    {/* Facilitator visibility (the legacy is_shared flag now only
                        controls what the forum facilitator can see; group sharing
                        is the accountability-group picker below) */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Visibility
                      </label>
                      <label className="flex items-start gap-3 text-sm text-gray-700 rounded-xl border border-gray-100 bg-off-white/40 p-3">
                        <input
                          type="checkbox"
                          id="is_shared"
                          checked={formData.is_shared}
                          onChange={(e) => setFormData({ ...formData, is_shared: e.target.checked })}
                          className="mt-1 rounded border-gray-300 text-navy focus:ring-navy"
                        />
                        <span>
                          <span className="font-medium text-navy">Visible to my facilitator</span>
                          <span className="block text-xs text-gray-500">
                            Your forum facilitator can see this goal and check in on it
                          </span>
                        </span>
                      </label>
                    </div>

                    <div className="rounded-xl border border-gray-100 bg-off-white/40 p-3">
                      {editingCommitment ? (
                        <ShareItemControl itemType="grow_goal" objectId={editingCommitment.id} />
                      ) : (
                        <ShareGroupPicker
                          selectedIds={shareGroupIds}
                          onChange={setShareGroupIds}
                        />
                      )}
                    </div>

                    {/* Advanced options disclosure */}
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setShowAdvanced(!showAdvanced)}
                        className="w-full px-4 py-3 flex items-center justify-between text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
                        aria-expanded={showAdvanced}
                      >
                        <span>Advanced options</span>
                        {showAdvanced ? (
                          <ChevronDown size={16} className="text-gray-400" />
                        ) : (
                          <ChevronRight size={16} className="text-gray-400" />
                        )}
                      </button>

                      {showAdvanced && (
                        <div className="px-4 pb-4 pt-1 space-y-4 border-t border-gray-100">
                          {/* Reminder cadence — chosen independently of the goal's frequency */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              When would you like to be reminded?
                            </label>
                            <div className="flex flex-wrap gap-2">
                              {([
                                { value: 'daily', label: 'Daily' },
                                { value: 'weekly', label: 'Weekly' },
                                { value: 'monthly', label: 'Monthly' },
                                { value: 'none', label: 'Never' },
                              ] as const).map((opt) => (
                                <button
                                  key={opt.value}
                                  type="button"
                                  onClick={() => setFormData({ ...formData, reminder_cadence: opt.value })}
                                  className={`px-3 py-2 rounded-lg border text-sm transition ${
                                    formData.reminder_cadence === opt.value
                                      ? 'border-navy bg-navy/5 text-navy'
                                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                                  }`}
                                >
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              How often we'll nudge you — separate from how often the goal happens.
                            </p>
                          </div>

                          {/* Reminder time-of-day — only relevant when reminders are on */}
                          {formData.reminder_cadence !== 'none' && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                What time of day?
                              </label>
                              <input
                                type="time"
                                value={formData.reminder_time}
                                onChange={(e) => setFormData({ ...formData, reminder_time: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent"
                              />
                              <p className="text-xs text-gray-500 mt-1">
                                We'll send the reminder around this time (Eastern Time).
                              </p>
                            </div>
                          )}

                          {/* Scheduling mode for weekly/monthly */}
                          {(formData.frequency === 'weekly' || formData.frequency === 'monthly') && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Scheduling type
                              </label>
                              <div className="grid grid-cols-2 gap-3">
                                <button
                                  type="button"
                                  onClick={() => setFormData({ ...formData, scheduling_mode: 'specific', target_count: 1 })}
                                  className={`flex items-center justify-center px-4 py-3 rounded-xl border text-sm transition ${
                                    formData.scheduling_mode === 'specific'
                                      ? 'border-navy bg-navy/5 text-navy'
                                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                                  }`}
                                >
                                  Specific days
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setFormData({ ...formData, scheduling_mode: 'flexible', preferred_days: [] })}
                                  className={`flex items-center justify-center px-4 py-3 rounded-xl border text-sm transition ${
                                    formData.scheduling_mode === 'flexible'
                                      ? 'border-navy bg-navy/5 text-navy'
                                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                                  }`}
                                >
                                  Flexible (any X days)
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Multi-day picker for specific weekly */}
                          {formData.frequency === 'weekly' && formData.scheduling_mode === 'specific' && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Which days? <span className="text-gray-400">(select one or more)</span>
                              </label>
                              <div className="grid grid-cols-7 gap-1">
                                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, idx) => (
                                  <button
                                    key={day}
                                    type="button"
                                    onClick={() => {
                                      const newDays = formData.preferred_days.includes(idx)
                                        ? formData.preferred_days.filter(d => d !== idx)
                                        : [...formData.preferred_days, idx].sort();
                                      setFormData({ ...formData, preferred_days: newDays });
                                    }}
                                    className={`py-2 pointer-coarse:py-3 px-1 rounded-lg text-xs font-medium transition ${
                                      formData.preferred_days.includes(idx)
                                        ? 'bg-navy text-white'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                                  >
                                    {day}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Target count for flexible weekly/monthly */}
                          {(formData.frequency === 'weekly' || formData.frequency === 'monthly') && formData.scheduling_mode === 'flexible' && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                How many times per {formData.frequency === 'weekly' ? 'week' : 'month'}?
                              </label>
                              <div className="flex items-center gap-3">
                                <input
                                  type="number"
                                  min={1}
                                  max={formData.frequency === 'weekly' ? 7 : 31}
                                  value={formData.target_count}
                                  onChange={(e) => setFormData({ ...formData, target_count: parseInt(e.target.value) || 1 })}
                                  className="w-24 px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent outline-none"
                                />
                                <span className="text-gray-500">times</span>
                              </div>
                            </div>
                          )}

                          {/* Preferred time for recurring commitments */}
                          {formData.frequency !== 'one_time' && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Preferred time <span className="text-gray-400">(optional)</span>
                              </label>
                              <input
                                type="time"
                                value={formData.preferred_time}
                                onChange={(e) => setFormData({ ...formData, preferred_time: e.target.value })}
                                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent outline-none"
                              />
                            </div>
                          )}

                          {/* End date toggle for recurring */}
                          {formData.frequency !== 'one_time' && (
                            <div>
                              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                <input
                                  type="checkbox"
                                  id="show_end_date"
                                  checked={formData.show_end_date}
                                  onChange={(e) => setFormData({ ...formData, show_end_date: e.target.checked, end_date: '' })}
                                  className="w-5 h-5 rounded border-gray-300 text-navy focus:ring-navy"
                                />
                                <label htmlFor="show_end_date" className="flex-1">
                                  <span className="font-medium text-gray-800">Set an end date?</span>
                                  <p className="text-xs text-gray-500">Leave unchecked for an ongoing Grow Goal</p>
                                </label>
                              </div>
                              {formData.show_end_date && (
                                <div className="mt-2">
                                  <input
                                    type="date"
                                    value={formData.end_date}
                                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent outline-none"
                                  />
                                </div>
                              )}
                            </div>
                          )}

                          {/* Category (optional) */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Related Area <span className="text-gray-400">(optional)</span>
                            </label>
                            <select
                              value={formData.category}
                              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent outline-none"
                            >
                              <option value="">Select a category</option>
                              {categories.map((cat) => (
                                <option key={cat.id} value={cat.id}>
                                  {cat.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      )}
                    </div>

                    </div>
                    <div className="flex gap-3 p-4 border-t border-gray-100">
                      <button
                        type="button"
                        onClick={() => {
                          setShowModal(false);
                          resetForm();
                        }}
                        className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={isSaving}
                        aria-label={editingCommitment ? 'Save Changes' : 'Create Grow Goal'}
                        className="flex-1 px-4 py-2 bg-navy text-white rounded-xl hover:bg-navy-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSaving ? 'Saving...' : editingCommitment ? 'Save Changes' : 'Save Goal'}
                      </button>
                    </div>
                  </form>
                  )}

                  {/* flex-auto, not flex-1: a 0% basis inside this max-height-only
                      modal collapses to zero height on iOS Safari. */}
                  {!editingCommitment && modalTab === 'jarvis' && (
                    <div className={`flex flex-col min-h-0 p-4 ${
                      keyboardOpen ? 'flex-1' : 'flex-auto h-[min(400px,50dvh)]'
                    }`}>
                      {/* Chat Messages */}
                      <div className="flex-1 min-h-0 overflow-y-auto space-y-3 mb-4">
                        {jarvisMessages.map((msg, idx) => (
                          <div
                            key={idx}
                            className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                          >
                            {msg.role === 'assistant' && (
                              <div className="w-8 h-8 bg-gradient-to-br from-navy to-gold rounded-full flex items-center justify-center flex-shrink-0">
                                <Sparkles className="text-white" size={14} />
                              </div>
                            )}
                            <div
                              className={`max-w-[80%] p-3 rounded-2xl ${
                                msg.role === 'user'
                                  ? 'bg-navy text-white rounded-br-md'
                                  : 'bg-off-white text-gray-800 rounded-bl-md'
                              }`}
                            >
                              <p className="whitespace-pre-wrap text-sm">
                                {msg.content.split(/(\*\*.*?\*\*)/g).map((part, i) =>
                                  part.length > 4 && part.startsWith('**') && part.endsWith('**')
                                    ? <strong key={i}>{part.slice(2, -2)}</strong>
                                    : part,
                                )}
                              </p>
                            </div>
                          </div>
                        ))}

                        {jarvisLoading && (
                          <div className="flex gap-3">
                            <div className="w-8 h-8 bg-gradient-to-br from-navy to-gold rounded-full flex items-center justify-center flex-shrink-0">
                              <Sparkles className="text-white" size={14} />
                            </div>
                            <div className="bg-off-white p-3 rounded-2xl rounded-bl-md">
                              <div className="flex gap-1">
                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                              </div>
                            </div>
                          </div>
                        )}
                        <div ref={jarvisChatEndRef} />
                      </div>

                      {/* Input */}
                      <div className="border-t border-gray-100 pt-4">
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <input
                              ref={jarvisInputRef}
                              type="text"
                              value={jarvisInput}
                              onChange={(e) => setJarvisInput(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && handleJarvisSend()}
                              placeholder="What do you want to commit to?"
                              className="w-full px-4 py-2 pr-11 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent outline-none text-sm"
                              disabled={jarvisLoading}
                            />
                            <MicButton
                              value={jarvisInput}
                              onChange={setJarvisInput}
                              className="absolute top-1/2 -translate-y-1/2 right-1.5"
                            />
                          </div>
                          <button
                            onClick={handleJarvisSend}
                            disabled={!jarvisInput.trim() || jarvisLoading}
                            className="px-4 py-2 bg-navy text-white rounded-lg hover:bg-navy-dark transition disabled:opacity-50"
                            aria-label="Send message"
                          >
                            <Send size={16} />
                          </button>
                        </div>
                        <p className="text-xs text-gray-400 mt-2 text-center">
                          Or <button type="button" onClick={() => setModalTab('manual')} className="text-navy hover:underline">create manually</button>
                        </p>
                      </div>
                    </div>
                  )}
                </>
              )}
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onConfirm={handleConfirmDelete}
        onCancel={() => { setDeleteConfirmOpen(false); setPendingDeleteId(null); }}
        title="Delete Grow Goal?"
        message="This action cannot be undone."
        confirmLabel="Delete"
        variant="danger"
      />
    </div>
  );
}
